import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  Calendar,
  Users,
  Settings,
  LogOut,
  User,
  Menu,
  X,
  ChevronDown,
  Building2,
  Target,
  FileText
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_client-hub-system-1/artifacts/b71v9ktt_image.png";

const navItems = [
  { path: "/", label: "TABLEAU DE BORD", icon: LayoutDashboard, hideForMecano: true },
  { path: "/planning", label: "PLANNING", icon: Calendar },
  { path: "/leads", label: "LEADS", icon: Target, hideForMecano: true },
  { path: "/devis", label: "DEVIS", icon: FileText, hideForMecano: true },
  { path: "/clients", label: "CLIENTS", icon: Users },
  { path: "/settings", label: "PARAMÈTRES", icon: Settings, adminOnly: true },
];

export default function Layout({ children }) {
  const { user, logout, agencies } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const filteredNavItems = navItems.filter(item => {
    if (item.adminOnly && user?.role !== "admin") return false;
    if (item.hideForMecano && user?.role === "mecano") return false;
    return true;
  });

  const userAgency = agencies.find(a => a.id === user?.agency_id);

  return (
    <div className="min-h-screen" style={{ background: '#000000' }}>
      {/* Safe area spacer for iOS PWA */}
      <div 
        className="fixed top-0 left-0 right-0 z-50"
        style={{ 
          height: 'env(safe-area-inset-top, 0px)',
          background: '#000000'
        }}
      />
      
      {/* Top Header with safe area for iOS */}
      <header 
        className="fixed left-0 right-0 border-b backdrop-blur-md z-50"
        style={{ 
          background: 'rgba(0, 0, 0, 0.98)', 
          borderColor: '#1A1A1A',
          top: 'env(safe-area-inset-top, 0px)'
        }}
      >
        <div className="h-14 px-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-3 -ml-2 text-gray-400 hover:text-[#FEED00] transition-colors rounded-lg"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              style={{ minWidth: '44px', minHeight: '44px' }}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            
            <Link to="/" className="flex items-center gap-2">
              <img 
                src={LOGO_URL} 
                alt="Horsepower Garage" 
                className="h-10 logo-horse"
              />
            </Link>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "text-[#FEED00]"
                      : "text-gray-500 hover:text-[#FEED00] hover:bg-white/5"
                  }`}
                  style={{ 
                    fontFamily: 'Bebas Neue, sans-serif',
                    letterSpacing: '0.05em',
                    background: isActive ? 'rgba(254, 237, 0, 0.1)' : 'transparent'
                  }}
                  data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* User Menu */}
          <div className="flex items-center gap-3">
            {userAgency && (
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-gray-500" style={{ background: '#0A0A0A' }}>
                <Building2 className="w-4 h-4" />
                {userAgency.city}
              </div>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 text-gray-300 hover:text-[#FEED00] hover:bg-white/5"
                  data-testid="user-menu-trigger"
                >
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: '#1A1A1A' }}
                  >
                    <User className="w-4 h-4" />
                  </div>
                  <span className="hidden sm:block text-sm">{user?.name}</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
                <DropdownMenuLabel className="text-gray-300">
                  <div className="flex flex-col">
                    <span>{user?.name}</span>
                    <span className="text-xs font-normal text-gray-600">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator style={{ background: '#1A1A1A' }} />
                <DropdownMenuItem className="text-gray-300 focus:bg-white/5 focus:text-white">
                  <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-600">
                    Rôle: <span className={user?.role === "admin" ? "text-[#FEED00]" : "text-green-400"}>{user?.role}</span>
                  </span>
                </DropdownMenuItem>
                <DropdownMenuSeparator style={{ background: '#1A1A1A' }} />
                <DropdownMenuItem 
                  onClick={logout}
                  className="text-red-400 focus:bg-red-500/10 focus:text-red-400 cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          <nav 
            className="absolute left-0 right-0 border-b p-4 space-y-1"
            style={{ 
              background: '#0A0A0A', 
              borderColor: '#1A1A1A',
              top: 'calc(56px + env(safe-area-inset-top, 0px))'
            }}
          >
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "text-[#FEED00] bg-[#FEED00]/10"
                      : "text-gray-400 hover:text-[#FEED00] hover:bg-white/5"
                  }`}
                  style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.05em' }}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {/* Main Content - adjust for header with safe area */}
      <main className="min-h-screen" style={{ paddingTop: 'calc(56px + env(safe-area-inset-top, 0px))' }}>
        <div className="p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
